/**
 * Session scope for user-dependent client caches (SIB-924).
 *
 * Every cached API response, in-flight request and user-dependent store belongs
 * to one authenticated session. On any authentication transition — logout,
 * login as a possibly different user, failed refresh / session expiry — the
 * scope is reset: the generation is bumped and every registered handler clears
 * its cache. A successful same-user token refresh is NOT a transition and must
 * not reset the scope.
 *
 * Modules register their own cleanup with {@link onSessionReset} at module
 * init, so lazy-loaded chunks keep their code-splitting (an unloaded module
 * has no cache to clear). Long-running work captures the generation when it
 * starts and checks {@link getSessionGeneration} before writing results into
 * a cache, so a response from a previous session can never repopulate the
 * next session's caches.
 *
 * This module must stay dependency-free to be importable from anywhere
 * (api client, stores, feature modules) without cycles.
 */

let generation = 0

const resetHandlers = new Set<() => void>()

/** The current session generation. Bumped on every session reset. */
export function getSessionGeneration(): number {
  return generation
}

/**
 * Register a cleanup that runs on every session reset. Returns an
 * unsubscribe function (rarely needed — module-level caches register once).
 */
export function onSessionReset(handler: () => void): () => void {
  resetHandlers.add(handler)
  return () => resetHandlers.delete(handler)
}

/**
 * End the current session scope: bump the generation (invalidating all
 * in-flight writes) and run every registered cleanup. Safe to call multiple
 * times; a failing handler never blocks the others.
 */
export function resetSessionScope(): void {
  generation += 1
  for (const handler of resetHandlers) {
    try {
      handler()
    } catch (e) {
      console.error('[sessionScope] reset handler failed:', e)
    }
  }
}
